import { useState } from 'react'
import { MessageMarkdown } from '@/components/message-markdown'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { formatDayLabel } from '@/lib/dates'
import { useRemoveMeeting } from '@/queries/clientMeetings'
import { SETTLED_SUMMARY, callTimeLabel } from '@/types/clientMeetings'
import type { ClientMeeting } from '@/types/clientMeetings'
import { DashCard, tdClass } from './dashboardParts'
import { MoveMeetingDialog } from './MoveMeetingDialog'
import { RequestsTable } from './RequestsTable'

function RemoveMeetingButton({ meeting }: { meeting: ClientMeeting }) {
  const [open, setOpen] = useState(false)
  const remove = useRemoveMeeting()
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          Remove from the hub
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove from the hub</DialogTitle>
          <DialogDescription>
            Remove {meeting.title} from the hub? Its summary and items are deleted here and it won't be fetched again. It stays in Fireflies, and can be
            restored from the help desk Settings page.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate({ meetingId: meeting._id }, { onSuccess: () => setOpen(false) })}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Summary({ meeting }: { meeting: ClientMeeting }) {
  if (meeting.summaryStatus === 'skipped') return <p className="text-[13px] italic text-etn-text-muted">Fireflies didn't write notes for this call</p>
  if (meeting.summaryStatus === 'failed') return <p className="text-[13px] italic text-etn-text-muted">Fireflies couldn't write notes for this call</p>
  if (!SETTLED_SUMMARY.includes(meeting.summaryStatus ?? '')) {
    return <p className="text-[13px] italic text-etn-text-muted">Fireflies is still writing the notes for this call</p>
  }
  return <MessageMarkdown>{meeting.overview || meeting.shortSummary || ''}</MessageMarkdown>
}

// List the call's Fireflies action items, collapsed under the client's requests.
function ActionItems({ meeting }: { meeting: ClientMeeting }) {
  if (!meeting.actionItems.length) return null
  return (
    <details className="border-t border-[var(--etn-gray-100)] px-[18px] py-2.5">
      <summary className="cursor-pointer text-[12.5px] text-etn-text-soft">Action items (from Fireflies) · {meeting.actionItems.length}</summary>
      <table className="mt-2 w-full border-collapse">
        <tbody className="divide-y divide-etn-border-row">
          {meeting.actionItems.map((item, index) => (
            <tr key={index}>
              <td className={`${tdClass} text-etn-text`}>{item.text}</td>
              <td className={`${tdClass} whitespace-nowrap text-etn-text-soft`}>{item.owner ?? '-'}</td>
              <td className={`${tdClass} whitespace-nowrap`}>
                {item.url && item.seconds !== null ? (
                  <a href={item.url} target="_blank" rel="noreferrer" className="font-medium tabular-nums text-etn-accent-text-active hover:underline">
                    {callTimeLabel(item.seconds)}
                  </a>
                ) : (
                  <span className="text-etn-text-muted">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

export function MeetingPanel({
  meeting,
  companyId,
  companyName,
  trackerEnabled,
  highlightRequestId,
}: {
  meeting: ClientMeeting
  companyId: string
  companyName: string
  trackerEnabled: boolean
  highlightRequestId?: string
}) {
  return (
    <DashCard
      testId="meeting-panel"
      source="call"
      title={`${meeting.title}, ${formatDayLabel(meeting.date)}`}
      right={
        <Button size="sm" variant="outline" asChild>
          <a href={meeting.url} target="_blank" rel="noreferrer">
            Open in Fireflies
          </a>
        </Button>
      }
    >
      <div className="border-b border-[var(--etn-gray-100)] px-[18px] py-3.5">
        <Summary meeting={meeting} />
      </div>
      <RequestsTable meeting={meeting} companyId={companyId} companyName={companyName} trackerEnabled={trackerEnabled} highlightId={highlightRequestId} />
      <ActionItems meeting={meeting} />
      <div className="flex flex-wrap justify-end gap-1.5 border-t border-[var(--etn-gray-100)] px-[18px] py-2.5">
        <MoveMeetingDialog meeting={meeting} companyId={companyId} />
        <RemoveMeetingButton meeting={meeting} />
      </div>
    </DashCard>
  )
}
